import React, { useState, useEffect } from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider,
  Chip,
  Grid
} from '@mui/material';
import {
  Send as SendIcon,
  Preview as PreviewIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import { useBrand } from '../../../../context/BrandContext';
import { useTheme } from '../../../../design-system/providers/ThemeProvider';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import NewsletterTagFilterControl, {
  buildNewsletterTagFilterPayload,
  filterNewsletterArticlesByTags
} from '../../NewsletterTagFilterControl/NewsletterTagFilterControl';

const QuickNewsletterDialog = ({ open, onClose, articles = [], onEmailSent }) => {
  const { activeBrandData } = useBrand();
  const { fontFamily: systemFontFamily } = useTheme();
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  
  const [form, setForm] = useState({
    subject: '',
    message: '',
    templateId: '',
    selectedContacts: [],
    groupByTags: false,
    showImages: true
  });
  const [tagFilter, setTagFilter] = useState({
    enabled: false,
    mode: 'exclude',
    tagIds: []
  });

  const filteredArticles = React.useMemo(
    () => filterNewsletterArticlesByTags(articles, tagFilter, availableTags),
    [articles, tagFilter, availableTags]
  );

  useEffect(() => {
    if (open) {
      loadInitialData();
      if (articles.length > 0) {
        setForm(prev => {
          const today = new Date();
          const formatter = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
          const formattedDate = formatter.format(today);
          const brandName = activeBrandData?.name || '';
          
          return {
            ...prev,
            subject: brandName ? `Newsletter ${brandName} - ${formattedDate}` : `Newsletter - ${formattedDate}`
          };
        });
      }
    } else {
      // Reset state when closing
      setPreviewHtml('');
      setSuccess('');
      setError('');
      setForm({
        subject: '',
        message: '',
        templateId: '',
        selectedContacts: [],
        groupByTags: false,
        showImages: true
      });
      setTagFilter({
        enabled: false,
        mode: 'exclude',
        tagIds: []
      });
    }
  }, [open, articles, activeBrandData]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [userRes, templRes, contRes, tagsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/newsletter/templates'),
        api.get('/newsletter/contacts?limit=9999'),
        api.get('/tags')
      ]);
      
      const availableTemplates = templRes.data.data || [];
      setTemplates(availableTemplates);
      setContacts(contRes.data.data || []);
      setAvailableTags(tagsRes.data.data || []);
      
      // Verificar se o usuário tem um template preferido
      const preferredTemplateId = userRes.data.data?.user?.preferredTemplateId;
      
      if (availableTemplates.length > 0) {
        let defaultTemp;
        
        // Primeiro: usar o template preferido do usuário
        if (preferredTemplateId) {
          defaultTemp = availableTemplates.find(t => t._id === preferredTemplateId && t.isActive);
        }
        
        // Segundo: usar o template marcado como padrão
        if (!defaultTemp) {
          defaultTemp = availableTemplates.find(t => t.isActive && t.isDefault);
        }
        
        // Terceiro: usar o primeiro disponível
        if (!defaultTemp) {
          defaultTemp = availableTemplates.find(t => t.isActive);
        }
        
        if (defaultTemp) {
          setForm(prev => ({ ...prev, templateId: defaultTemp._id.toString() }));
        }
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Falha ao carregar templates ou contatos');
    } finally {
      setLoading(false);
    }
  };

  // Salvar preferência de template
  const saveTemplatePreference = async (templateId) => {
    try {
      await api.put('/auth/preferred-template', { templateId });
    } catch (err) {
      console.warn('Erro ao salvar preferência de template:', err);
    }
  };

  const handlePreview = async () => {
    if (!form.templateId) {
      setError('Selecione um template para o preview');
      return;
    }
    if (filteredArticles.length === 0) {
      setError('O filtro por tags removeu todos os artigos');
      return;
    }

    try {
      setPreviewLoading(true);
      setError('');
      const response = await api.post('/newsletter/preview', {
        subject: form.subject,
        message: form.message,
        templateId: form.templateId,
        articleIds: filteredArticles.map(a => a._id),
        groupByTags: form.groupByTags,
        showImages: form.showImages,
        tagsOrder: activeBrandData?.newsletterOrderTemplate?.tagsOrder || [],
        newsletterTagFilter: buildNewsletterTagFilterPayload(
          tagFilter,
          availableTags,
          activeBrandData?.newsletterOrderTemplate?.tagsOrder || []
        ),
        fontFamily: systemFontFamily
      });

      if (response.data.success) {
        setPreviewHtml(response.data.html);
      } else {
        setError('Falha ao gerar preview');
      }
    } catch (err) {
      console.error('Erro no preview:', err);
      setError('Erro ao gerar visualização no servidor');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!form.subject.trim()) {
      setError('O assunto é obrigatório');
      return;
    }
    if (form.selectedContacts.length === 0) {
      setError('Selecione pelo menos um destinatário');
      return;
    }

    if (filteredArticles.length === 0) {
      setError('O filtro por tags removeu todos os artigos');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const newsletterData = {
        subject: form.subject.trim(),
        message: form.message?.trim() || '',
        articleIds: filteredArticles.map(a => a._id),
        recipientIds: form.selectedContacts,
        templateId: form.templateId,
        groupByTags: form.groupByTags,
        showImages: form.showImages,
        tagsOrder: activeBrandData?.newsletterOrderTemplate?.tagsOrder || [],
        newsletterTagFilter: buildNewsletterTagFilterPayload(
          tagFilter,
          availableTags,
          activeBrandData?.newsletterOrderTemplate?.tagsOrder || []
        ),
        fontFamily: systemFontFamily
      };

      const response = await api.post('/newsletter/create', newsletterData);
      
      if (response.data.success && response.data.data._id) {
        await api.post(`/newsletter/send/${response.data.data._id}`);
        setSuccess('Newsletter enviada com sucesso!');
        setTimeout(() => {
          onClose();
          if (onEmailSent) onEmailSent();
        }, 2000);
      }
    } catch (err) {
      console.error('Erro ao enviar:', err);
      setError(err.response?.data?.message || 'Erro ao enviar newsletter');
    } finally {
      setLoading(false);
    }
  };

  const toggleContact = (id) => {
    setForm(prev => ({
      ...prev,
      selectedContacts: prev.selectedContacts.includes(id)
        ? prev.selectedContacts.filter(cId => cId !== id)
        : [...prev.selectedContacts, id]
    }));
  };

  return (
    <PlatformDialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <PlatformDialogTitle
        title="Configuração de Envio Rápido"
        icon={<SendIcon />}
        onClose={onClose}
      />
      
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Configuração */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2" color="primary">Informações Básicas</Typography>
            
            <TextField
              fullWidth
              label="Assunto do Email"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              size="small"
            />
            
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Mensagem Personalizada (opcional)"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              size="small"
            />
            
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Template de Design
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {templates.filter(t => t.isActive).map(t => {
                  const isSelected = form.templateId === t._id.toString();
                  return (
                    <Chip
                      key={t._id}
                      label={t.name}
                      onClick={() => {
                        setForm(prev => ({ ...prev, templateId: t._id.toString() }));
                        saveTemplatePreference(t._id.toString());
                      }}
                      icon={<EmailIcon />}
                      variant={isSelected ? 'filled' : 'outlined'}
                      color={isSelected ? 'warning' : 'default'}
                      sx={{ 
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 1,
                        '& .MuiChip-icon': { color: isSelected ? '#000' : 'text.secondary' }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            <FormControlLabel
              control={<Checkbox checked={form.groupByTags} onChange={(e) => setForm({ ...form, groupByTags: e.target.checked })} />}
              label="Agrupar por Tags"
            />
            
            <FormControlLabel
              control={<Checkbox checked={form.showImages} onChange={(e) => setForm({ ...form, showImages: e.target.checked })} />}
              label="Mostrar miniaturas dos artigos"
            />

            <NewsletterTagFilterControl
              articles={articles}
              availableTags={availableTags}
              value={tagFilter}
              onChange={setTagFilter}
              tagsOrder={activeBrandData?.newsletterOrderTemplate?.tagsOrder || []}
              compact
            />

            <Divider />
            
            <Typography variant="subtitle2" color="primary">Destinatários ({form.selectedContacts.length})</Typography>
            <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
              <List size="small" disablePadding>
                {contacts.map(contact => (
                  <ListItem 
                    key={contact._id} 
                    dense 
                    button 
                    onClick={() => toggleContact(contact._id)}
                  >
                    <ListItemText primary={contact.name} secondary={contact.email} />
                    <ListItemSecondaryAction>
                      <Checkbox 
                        edge="end" 
                        checked={form.selectedContacts.includes(contact._id)} 
                        onChange={() => toggleContact(contact._id)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>

            <Button 
              variant="contained" 
              color="secondary" 
              onClick={handlePreview}
              disabled={previewLoading || loading}
              startIcon={<PreviewIcon />}
            >
              {previewLoading ? 'Gerando Preview...' : 'Gerar Preview'}
            </Button>
          </Box>

          {/* Preview */}
          <Box sx={{ flex: 1.5, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>Prévia do Conteúdo</Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                height: 500, 
                bgcolor: '#f5f5f5', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  title="Newsletter Preview"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {previewLoading ? 'Carregando visualização...' : 'Clique em "Gerar Preview" para visualizar a newsletter antes de enviar.'}
                  </Typography>
                  {previewLoading && <CircularProgress size={24} sx={{ mt: 2 }} />}
                </Box>
              )}
            </Paper>
            
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Artigos incluídos: {filteredArticles.length}
                {filteredArticles.length !== articles.length ? ` (${articles.length} selecionados)` : ''}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {filteredArticles.slice(0, 3).map(a => (
                  <Chip key={a._id} label={a.title} size="small" sx={{ maxWidth: 150 }} />
                ))}
                {filteredArticles.length > 3 && <Chip label={`+${filteredArticles.length - 3}`} size="small" />}
              </Box>
            </Box>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSend} 
          disabled={loading || form.selectedContacts.length === 0}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
        >
          Enviar Agora
        </Button>
      </DialogActions>
    </PlatformDialog>
  );
};

export default QuickNewsletterDialog;
